import type { EditorCommands } from './commands.js';
import type { AppState } from '../state/appState.js';
import type { DataGraphInstance } from '../graph/dataGraph.js';
import type { CommandBatch } from './commandHistory.js';
import { currentDataGraphId } from '../state/runtimeGraphSelectors.js';

export function rewriteDataOutputBindings(
  appState: AppState,
  commands: Pick<EditorCommands, 'setDataOwner'>,
  graphId: string,
  previous: string,
  next: string | null,
): void {
  const rewrite = (instance: DataGraphInstance): DataGraphInstance | null => {
    if (instance.use !== graphId || !(previous in instance.bindings)) return null;
    const bindings = { ...instance.bindings };
    const target = bindings[previous];
    delete bindings[previous];
    if (next) bindings[next] = target;
    return { ...instance, bindings };
  };
  if (appState.data) {
    const updated = rewrite(appState.data);
    if (updated) commands.setDataOwner({ type: 'document' }, updated);
  }
  const visitedComponents = new Set<string>();
  const visit = (scene: AppState['scene']): void => {
    for (const node of Object.values(scene.nodes)) {
      if (node.behavior) {
        const updated = rewrite(node.behavior);
        if (updated) commands.setDataOwner({ type: 'scene', nodeId: node.id }, updated);
      }
      if (node.kind !== 'instance' || visitedComponents.has(node.componentId)) continue;
      visitedComponents.add(node.componentId);
      const component = appState.componentLibrary[node.componentId];
      if (component) visit(component.scene);
    }
  };
  visit(appState.scene);
}

function rewriteDataInputValues(
  appState: AppState,
  commands: Pick<EditorCommands, 'setDataOwner'>,
  graphId: string,
  previous: string,
  next: string | null,
): void {
  const rewrite = (instance: DataGraphInstance): DataGraphInstance | null => {
    if (instance.use !== graphId || !instance.inputs || !(previous in instance.inputs)) return null;
    const inputs = { ...instance.inputs };
    const value = inputs[previous];
    delete inputs[previous];
    if (next) inputs[next] = value;
    return { ...instance, inputs };
  };
  if (appState.data) {
    const updated = rewrite(appState.data);
    if (updated) commands.setDataOwner({ type: 'document' }, updated);
  }
  const visited = new Set<string>();
  const visit = (scene: AppState['scene']): void => {
    for (const node of Object.values(scene.nodes)) {
      if (node.behavior) {
        const updated = rewrite(node.behavior);
        if (updated) commands.setDataOwner({ type: 'scene', nodeId: node.id }, updated);
      }
      if (node.kind === 'instance' && !visited.has(node.componentId)) {
        visited.add(node.componentId);
        const component = appState.componentLibrary[node.componentId];
        if (component) visit(component.scene);
      }
    }
  };
  visit(appState.scene);
}

export function renameDataInput(
  appState: AppState,
  commands: Pick<EditorCommands, 'setNodeData' | 'setDataOwner'>,
  commandBatch: CommandBatch,
  nodeId: string,
  value: string,
): boolean {
  const graphId = currentDataGraphId(appState);
  if (appState.editorGraphKind !== 'data' || !graphId) return false;
  const node = appState.nodes.find(candidate => candidate.id === nodeId && candidate.type === 'Input');
  if (!node) return false;
  const previous = String(node.data.name || '');
  const next = value.trim();
  if (!next || next === previous || appState.nodes.some(candidate => candidate.id !== nodeId && candidate.type === 'Input' && String(candidate.data.name || '') === next)) return false;
  commandBatch.run('Rename Data Input', () => {
    commands.setNodeData(nodeId, 'name', next);
    rewriteDataInputValues(appState, commands, graphId, previous, next);
  });
  return true;
}

export function setDataOutputType(
  appState: AppState,
  commands: Pick<EditorCommands, 'disconnectEdges' | 'setNodeData'>,
  commandBatch: CommandBatch,
  nodeId: string,
  nextType: string,
): boolean {
  const node = appState.nodes.find(candidate => candidate.id === nodeId && candidate.type === 'Output');
  if (!node || appState.editorGraphKind !== 'data') return false;
  const previousType = String(node.data.inputType || 'float');
  if (previousType === nextType) return false;
  const incoming = appState.edges.filter(edge => edge.toNode === nodeId);
  commandBatch.run('Set Output Type', () => {
    if (incoming.length > 0) commands.disconnectEdges(incoming);
    commands.setNodeData(nodeId, 'inputType', nextType);
    commands.setNodeData(nodeId, 'displayType', nextType);
  });
  return true;
}

export function renameDataOutput(
  appState: AppState,
  commands: Pick<EditorCommands, 'setNodeData' | 'setDataOwner'>,
  commandBatch: CommandBatch,
  nodeId: string,
  value: string,
): boolean {
  const graphId = currentDataGraphId(appState);
  if (appState.editorGraphKind !== 'data' || !graphId) return false;
  const node = appState.nodes.find(candidate => candidate.id === nodeId && candidate.type === 'Output');
  if (!node) return false;
  const previous = String(node.data.inputName || 'Output');
  const next = value.trim();
  if (
    !next
    || next === previous
    || appState.nodes.some(candidate => (
      candidate.id !== nodeId
      && candidate.type === 'Output'
      && String(candidate.data.inputName || 'Output') === next
    ))
  ) return false;
  commandBatch.run('Rename Output', () => {
    commands.setNodeData(nodeId, 'inputName', next);
    rewriteDataOutputBindings(
      appState,
      commands,
      graphId,
      previous,
      next,
    );
  });
  return true;
}

export function deleteDataOutput(
  appState: AppState,
  commands: Pick<EditorCommands, 'deleteNode' | 'setDataOwner'>,
  commandBatch: CommandBatch,
  nodeId: string,
): boolean {
  const graphId = currentDataGraphId(appState);
  if (appState.editorGraphKind !== 'data' || !graphId) return false;
  const node = appState.nodes.find(candidate => candidate.id === nodeId && candidate.type === 'Output');
  if (!node) return false;
  const name = String(node.data.inputName || 'Output');
  commandBatch.run('Delete Output', () => {
    commands.deleteNode(nodeId);
    rewriteDataOutputBindings(appState, commands, graphId, name, null);
  });
  return true;
}
